import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { filter } from 'rxjs/internal/operators/filter';
import { take } from 'rxjs/internal/operators/take';
import { CuralateService } from './curalate.service';

@Directive({
    selector: 'curalate-gallery',
    standalone: true
})
export class CuralateGalleryDirective implements AfterViewInit, OnChanges, OnDestroy {
    @Input()
    galleryId?: string;

    @Input()
    filter?: string;

    @Output()
    loaded = new EventEmitter<boolean>();

    status = new Status();

    protected initiated = false;
    protected subscriptionViewport?: Subscription;
    protected onUpdate = this.timeoutService.debounce(() => this._onUpdate(), 100);

    constructor(
        protected curalate: CuralateService,
        protected changeDetectorRef: ChangeDetectorRef,
        protected timeoutService: TimeoutService,
        protected elementRef: ElementRef,
        private visibilityService: VisibilityService,
        private app: App
    ) {}

    ngOnChanges() {
        if (this.initiated) {
            this.onUpdate();
        }
    }

    ngAfterViewInit() {
        this.subscriptionViewport = this.visibilityService
            .isInView(this.elementRef, {
                interaction: { rootMargin: '1000px' },
                initial: { rootMargin: '50px' }
            })
            .pipe(
                filter<boolean>(visible => visible),
                take(1)
            )
            .subscribe(() => {
                this.initiated = true;
                this.onUpdate();
            });
    }

    ngOnDestroy() {
        this.destroy();

        this.subscriptionViewport?.unsubscribe();
    }

    /**
     * Destroy curalate experience
     * @returns {Promise<boolean}
     */
    protected destroy(): Promise<boolean> {
        return new Promise(resolve => {
            if (this.galleryId && this.curalate.crl8?.destroyExperience) {
                try {
                    this.curalate.crl8
                        .destroyExperience(this.galleryId)
                        .then(() => {
                            resolve(true);
                        })
                        .catch(() => {
                            resolve(false);
                        });
                } catch (e) {
                    this.app.logger.error(e);
                    resolve(false);
                }
            } else {
                resolve(false);
            }
        });
    }

    /**
     * Check if curalate gallery has content
     * @returns {boolean}
     */
    hasContent(): boolean {
        const container = this.getContainerElement();

        if (container) {
            return !!container.querySelector('[role="listitem"]');
        }

        return false;
    }

    /**
     * On inputs change callback
     * First clears component and create new one if possible
     */
    protected _onUpdate(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        const container = this.getContainerElement();
        const galleryId = this.galleryId;

        if (container && galleryId) {
            this.curalate.loadScript(this.status, () => {
                this.curalate.crl8?.ready(() => {
                    const initExperience = () => {
                        const html = `<div data-crl8-container-id="${galleryId}" ${
                            this.filter ? 'data-crl8-filter="' + this.filter + '"' : ''
                        }></div>`;
                        container.innerHTML = html;
                        this.detectChanges();
                        this.curalate.crl8
                            ?.createExperience(galleryId)
                            .then(() => this.emit(true))
                            .catch(e => {
                                this.app.logger.error(e);
                                this.emit(false);
                            });
                    };

                    this.curalate.crl8
                        ?.getExperience(galleryId)
                        .then(experience => {
                            if (experience) {
                                this.destroy()
                                    .then(() => {
                                        initExperience();
                                    })
                                    .catch(() => this.emit(false));
                            } else {
                                initExperience();
                            }
                        })
                        .catch(e => {
                            this.app.logger.error(e);
                            initExperience();
                        });
                });
            });
        } else {
            this.emit(false, true);
        }

        this.detectChanges();
    }

    /**
     * Cancel load promise
     */
    protected cancelLoad(): void {
        if (this.status?.promise?.cancel) {
            this.status.promise.cancel();
        }
    }

    /**
     * Emits loaded event
     * @param {boolean} value
     * @param {boolean} withCancel - if true load promise will be canceled
     */
    protected emit(value: boolean, withCancel = false): void {
        this.loaded.emit(value);

        if (withCancel) {
            this.cancelLoad();
        }

        this.detectChanges();
    }

    /**
     * Returns container html element
     * @returns {HTMLElement | undefined}
     */
    protected getContainerElement(): HTMLElement | undefined {
        return this.elementRef?.nativeElement;
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
}
